/**
 * The wall as the workspace (Part 04 Task 9; Layout C, 2026-10-19; one
 * header since Part 09; the frost behind it since plan 11): the sticky header
 * (scope, search with notifications, +, theme, close; ⌘K focuses the
 * search), the Archived bar, the Pinned rows, the masonry gallery (one
 * feed, new tasks and things among its frames), the empty state and the loading skeleton, the card menu, image
 * paste and file drops (the backend places the frames).
 *
 * The scope (the project, Everything, Archived) lives in
 * `stores/wall-scope-store.ts`, so Everything outlives the remount on a
 * project switch; another project's card there opens in its project.
 *
 * Mounted in every desktop mode on the wall path; hidden in place where
 * `wallVisibleHere()` is false, so its feeds and scroll persist across mode
 * changes. Cards open in focus over the wall (`openFrames`); while that
 * focus shows here the wall is inert, and new work arrives at the top
 * (`useWallArrivals`). Each show is one arrival (`useWallReveal`): the frost
 * comes at once, and the wall fades in over it once its first page, Pinned
 * and new cards are in place. ⌘/Ctrl- or Shift-click selects cards; a selected
 * card's menu then acts on the whole selection, and Esc clears it. The
 * shown wall takes input across its whole area, gaps included (plan 11
 * A4): a click on empty wall space does nothing, a right-click there opens
 * the add menu (the header +'s list) and a drop there adds the files
 * (`addFilesToWall`), on the desktop through `useNativeFileDrop`
 * (`addPathsToWall`, which links PDFs and other files where they are). A
 * right-click on a Pinned tile is the tile's own: its menu, or nothing.
 */
import { MenuSurfaceContext } from "@/components/ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type MouseEvent,
} from "react";
import { openFrameInProject } from "@/components/layout/cross-project-navigation";
import { preloadDocEditor } from "@/components/shapes/doc-frame/doc-editor.lazy";
import { HtmlPosterBackfill } from "@/components/shapes/html-frame/html-poster-backfill";
import { uploadFrameImage } from "@/components/shapes/shared/frame-image-upload";
import { useDocumentImagePaste } from "@/hooks/use-document-image-paste";
import { useNativeFileDrop } from "@/hooks/use-native-file-drop";
import { useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { useCardReturnFrameId } from "@/lib/focus/focus-card-return";
import { useFocusVisibleHere } from "@/lib/focus/use-focus-visible-here";
import { isTauri } from "@/lib/is-tauri";
import { openFrames } from "@/lib/open-frames";
import { registerWallRevealer } from "@/lib/wall-reveal";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useProjectStore } from "@/stores/project-store";
import { useSelectionStore } from "@/stores/selection-store";
import {
  foreignWallFrame,
  useWallScope,
  useWallScopeStore,
  type WallScope,
} from "@/stores/wall-scope-store";
import { useEnsureWallTools } from "./use-ensure-wall-tools";
import { useMemoryTool } from "./use-memory-tool";
import { useWallArrivals } from "./use-wall-arrivals";
import { useWallFeedView } from "./use-wall-feed-view";
import {
  useWallFeedWithNewItems,
  useWallNewItems,
  useWallNewItemsReady,
} from "./use-wall-new-items";
import { useWallReveal } from "./use-wall-reveal";
import { useWallSearchShortcut } from "./use-wall-search-shortcut";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import type { WallCardMenuTarget } from "./wall-card-menu";
import { WallAddMenu } from "./wall-add-menu";
import { WallCardMenuHost } from "./wall-card-menu-host";
import {
  WallEmptyState,
  WallFeedEnd,
  WallFeedError,
  WallSkeleton,
} from "./wall-feed-states";
import { WallHeader } from "./wall-header";
import { WallMasonry, type WallMasonryHandle } from "./wall-masonry";
import {
  WallMultiSelectionMenu,
  type WallMultiSelectionMenuTarget,
} from "./wall-multi-selection-menu";
import { WallScopeBar } from "./wall-scope-bar";
import { useWallSelectionEscape } from "./wall-selection";
import { WallToolsRow } from "./wall-tools-row";
import {
  addFilesToWall,
  addPathsToWall,
  droppedFiles,
  reportSkippedFiles,
} from "./wall-file-import";
import { visibleWallCard } from "./wall-view-adapter";
import "./wall-surface.css";

interface WallViewProps {
  projectId: string;
  /** `wallVisibleHere()`: hidden, the wall is inert and invisible. */
  visible: boolean;
}

const PROJECT: WallScope = { kind: "project" };
const HIDDEN_STYLE: CSSProperties = { visibility: "hidden" };
/**
 * Right-clicks here keep the browser's own menu (text), belong to a Pinned
 * tile (its menu or none) or do nothing.
 */
const NO_PANE_MENU =
  "input,textarea,[contenteditable='true'],a,button,[data-wall-tile]";

/**
 * Another project's card opens in its project: editing in focus writes
 * through the current project, so the switch comes first.
 */
function openInItsProject(frame: FrameData): void {
  const projectId = frame.spaceId ?? null;
  const project = useProjectStore
    .getState()
    .projects.find((candidate) => candidate.id === projectId);
  void openFrameInProject({
    id: frame.frameId,
    projectId,
    projectName: project?.name ?? null,
  });
}

/**
 * A Pinned tile's handle: a pin's Open, or the link in a tool's title line.
 * The masonry does not place the Pinned row.
 */
function findPinnedHandle(
  root: HTMLElement | null,
  id: string,
): HTMLElement | null {
  const testId = `wall-tool-tile-${id}`;
  for (const tile of root?.querySelectorAll<HTMLElement>("[data-wall-tile]") ??
    []) {
    if (tile.dataset.testid !== testId) continue;
    return tile.querySelector<HTMLElement>(
      "[data-testid^='wall-card-open-'], [data-testid^='wall-pin-open-']",
    );
  }
  return null;
}

export function WallView({ projectId, visible }: WallViewProps) {
  const tFrames = useT(framesSharedMessages);
  // The scope outlives this view's remount on a project switch.
  const scope = useWallScope(projectId);
  const setScope = useCallback(
    (next: WallScope) => useWallScopeStore.getState().setScope(next, projectId),
    [projectId],
  );
  const [query, setQuery] = useState("");
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [menu, setMenu] = useState<WallCardMenuTarget | null>(null);
  const [multiMenu, setMultiMenu] =
    useState<WallMultiSelectionMenuTarget | null>(null);
  const [addMenu, setAddMenu] = useState<ContextMenuAnchor | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const masonryRef = useRef<WallMasonryHandle>(null);

  const searching = query.trim() !== "";
  const projectFeed = scope.kind === "project" && !searching;
  // Everything keeps the current project's Pinned row (decision 4); a
  // search narrows its tools in place (decision 2).
  const pinnedShown = scope.kind !== "archived";

  const feed = useWallFeedView(projectId, scope, query, visible);
  const memoryTool = useMemoryTool();
  const memory = memoryTool.available;
  useEnsureWallTools({
    projectId,
    toolsLoaded: feed.toolsLoaded,
    memory,
  });
  // New tasks and things show once among the frames (decision 5).
  const newItems = useWallNewItems({
    projectId,
    scope,
    query,
    visible,
    memory,
  });
  const newItemsReady = useWallNewItemsReady(projectId, scope, memory);
  const newItemsByKey = useMemo(
    () => new Map(newItems.map((item) => [item.key, item])),
    [newItems],
  );
  const ids = useWallFeedWithNewItems(
    feed.ids,
    newItems,
    feed.hasMore,
    searching,
  );
  const focusOver = useFocusVisibleHere();
  const interactive = visible && !focusOver;
  // The desktop marks the whole wall only in Workspace mode; elsewhere
  // (Peek) its gaps leave the desktop's clicks alone.
  const workspace = useDesktopMode((state) => state.mode === "denk");
  const takesGaps = interactive && (workspace || !isTauri());
  const arrivals = useWallArrivals({
    ids: feed.ids,
    // New cards rise at the top of the project's feed and of Everything.
    tracking: !searching && scope.kind !== "archived",
    loading: feed.loading,
    masonryRef,
  });
  const { flash } = arrivals;

  const openFrame = useCallback((id: string) => {
    // A plain open ends a multi-selection.
    useSelectionStore.getState().clearMulti();
    const foreign = foreignWallFrame(id);
    if (foreign) {
      openInItsProject(foreign);
      return;
    }
    const fromCard = visibleWallCard(id) ?? undefined;
    if (openFrames({ frameIds: [id], origin: "card", fromCard })) {
      setOpenedId(id);
    }
  }, []);
  // The opened item stays marked until focus here closes, and a card until
  // its return into its slot settles; a later focus opened elsewhere marks
  // nothing.
  if (!focusOver && openedId !== null) setOpenedId(null);
  const returningId = useCardReturnFrameId();
  // A selected card of a multi-selection opens the selection's menu.
  const openMenu = useCallback((frameId: string, anchor: ContextMenuAnchor) => {
    const frameIds = useSelectionStore.getState().multiSelectedFrameIds;
    if (frameIds.length > 1 && frameIds.includes(frameId)) {
      setMultiMenu({ frameIds, anchor });
      return;
    }
    setMenu({ frameId, anchor });
  }, []);
  useWallSelectionEscape(interactive && menu === null && multiMenu === null);
  // A Doc opened from the wall shows its editor at once, not the excerpt.
  useEffect(preloadDocEditor, []);
  // A selection belongs to the cards shown: another scope or search starts
  // without one.
  useEffect(() => {
    useSelectionStore.getState().clearMulti();
  }, [scope.kind, query]);
  const jump = useCallback(
    (id: string) => {
      if (masonryRef.current?.scrollToCard(id)) flash([id]);
    },
    [flash],
  );
  useEffect(
    () =>
      registerWallRevealer((id, { focus }) => {
        if (!focus) {
          jump(id);
          return;
        }
        if (masonryRef.current?.focusCard(id)) return;
        const handle = findPinnedHandle(rootRef.current, id);
        handle?.scrollIntoView({ block: "nearest" });
        handle?.focus({ preventScroll: true });
      }),
    [jump],
  );

  const { loadMore } = feed;

  // Paste stays images only; an unsupported one gets its own toast.
  const pasteImages = useCallback(
    (files: File[]) => {
      for (const file of files) {
        void uploadFrameImage(file, { projectId, spaceId: projectId });
      }
    },
    [projectId],
  );
  // An empty wall that is loading again shows the skeleton, not the error.
  const feedFailed = feed.failed && !(feed.loading && ids.length === 0);
  // Shown, the wall waits for its cards, Pinned and the new cards among
  // them; a failed load counts as settled.
  const revealed = useWallReveal(
    visible,
    (ids.length > 0 || !feed.loading || feedFailed) &&
      (!pinnedShown || feed.toolsSettled) &&
      memoryTool.known &&
      newItemsReady,
  );
  useDocumentImagePaste(interactive, pasteImages);
  useWallSearchShortcut(interactive, rootRef, searchRef, projectId);
  const handleDragOver = (event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };
  const handleContextMenu = (event: MouseEvent) => {
    // A card opened its own menu; a portaled menu or dialog is not the wall.
    // Before its reveal a click on the wall does nothing.
    if (event.defaultPrevented || !interactive || !revealed) return;
    if (!event.currentTarget.contains(event.target as Node)) return;
    if (event.target instanceof Element && event.target.closest(NO_PANE_MENU)) {
      return;
    }
    event.preventDefault();
    setAddMenu({ x: event.clientX, y: event.clientY });
  };
  const handleDrop = (event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    const { files, skipped } = droppedFiles(event.dataTransfer);
    reportSkippedFiles(skipped);
    void addFilesToWall(files, projectId);
  };
  // The desktop app's OS drops skip the DOM events above.
  useNativeFileDrop(rootRef, takesGaps, {
    onDrop: (paths) => addPathsToWall(paths, projectId),
  });

  // The workspace view adapter's overview and settle: the project, unfiltered.
  const showTop = useCallback(() => {
    setScope(PROJECT);
    setQuery("");
  }, [setScope]);

  // The sticky header is the scroller's own child, so it stays pinned while
  // the dashboard and the gallery scroll under it.
  const header = (
    <>
      <WallHeader
        query={query}
        onQueryChange={setQuery}
        searchRef={searchRef}
        scope={scope}
        onScopeChange={setScope}
        newCount={arrivals.newCount}
        onShowNew={arrivals.showNew}
        addMenuOpen={addMenu !== null}
        onAddMenu={setAddMenu}
      />
      <div className="wall-column pt-appkit-xs">
        {scope.kind === "archived" ? (
          <WallScopeBar
            title={tFrames("archived")}
            onClose={() => setScope(PROJECT)}
          />
        ) : null}
        {pinnedShown ? (
          <WallToolsRow
            projectId={projectId}
            query={query.trim()}
            allProjects={scope.kind === "all"}
            live={visible}
            memory={memory}
            flashingIds={arrivals.flashingIds}
            openedId={openedId}
            onOpen={openFrame}
            onMenu={openMenu}
          />
        ) : null}
      </div>
    </>
  );

  const footer = (
    <>
      {ids.length === 0 || feedFailed ? (
        <div className="wall-column">
          {feedFailed ? (
            <WallFeedError onRetry={feed.retry} />
          ) : feed.loading ? (
            <WallSkeleton />
          ) : (
            <WallEmptyState scope={scope} searching={searching} />
          )}
        </div>
      ) : null}
      <WallFeedEnd
        active={visible && ids.length > 0}
        loaded={feed.ids.length}
        onReach={loadMore}
      />
    </>
  );

  return (
    // Every menu, flyout and pocket opened from the wall wears its card.
    <MenuSurfaceContext.Provider value="card">
      <div
        ref={rootRef}
        // The desktop overlay passes clicks through except over marked
        // surfaces: the shown wall marks its whole area, so its gaps sit on
        // the frost and take clicks, right-clicks, drops and the wheel.
        // Hidden or under focus (inert), nothing here takes input. Its own
        // stacking context keeps its sticky header and pills under the focus
        // overlay drawn after it. Shown, it fades in (200 ms) once revealed.
        className={cn(
          "absolute inset-0 isolate motion-safe:transition-opacity motion-safe:duration-200 motion-safe:ease-out",
          revealed ? "opacity-100" : "opacity-0",
          interactive && "pointer-events-auto",
        )}
        style={visible ? undefined : HIDDEN_STYLE}
        data-revealed={revealed || undefined}
        inert={!interactive || undefined}
        aria-hidden={!interactive || undefined}
        data-interactive={takesGaps ? "true" : undefined}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onContextMenu={handleContextMenu}
        data-testid="wall-view"
      >
        {/* Until the reveal, the root still takes the wall's input, but the
            unseen cards and tiles take no click and no keyboard focus. */}
        <div
          className={cn("h-full", !revealed && "pointer-events-none")}
          inert={!revealed || undefined}
          data-testid="wall-content"
        >
          <WallMasonry
            ref={masonryRef}
            ids={ids}
            newItems={newItemsByKey}
            header={header}
            footer={footer}
            flashingIds={arrivals.flashingIds}
            risingIds={arrivals.risingIds}
            openedId={openedId ?? returningId}
            onScroll={arrivals.onScroll}
            projectFeed={projectFeed}
            onShowTop={showTop}
            onOpen={openFrame}
            onMenu={openMenu}
          />
        </div>
        <WallCardMenuHost
          target={interactive ? menu : null}
          surface="card"
          onClose={() => setMenu(null)}
        />
        <WallMultiSelectionMenu
          target={interactive ? multiMenu : null}
          onClose={() => setMultiMenu(null)}
        />
        <WallAddMenu
          projectId={projectId}
          anchor={interactive ? addMenu : null}
          onClose={() => setAddMenu(null)}
        />
        {visible && <HtmlPosterBackfill />}
      </div>
    </MenuSurfaceContext.Provider>
  );
}
